import { createFileRoute } from "@tanstack/react-router";
import { Section, EyebrowHeading } from "@/components/site/Section";
import { Button } from "@/components/site/Button";

import { ContactForm } from "@/components/site/ContactForm";
import { breadcrumbs, faqPage, serviceSchema, videoObject, aeoGeo } from "@/lib/seo/schema";
import { ogImageMeta } from "@/lib/seo/og-image";
import { BrollopPackages } from "@/components/site/brollop/BrollopPackages";
import { BrollopTimeline } from "@/components/site/brollop/BrollopTimeline";
import { BrollopCalculator } from "@/components/site/brollop/BrollopCalculator";
import { BrollopHeroVideo } from "@/components/site/brollop/BrollopHeroVideo";
import { BrollopMoodStrip } from "@/components/site/brollop/BrollopMoodStrip";
import { BrollopFloatingStills } from "@/components/site/brollop/BrollopFloatingStills";
import { BrollopFullBleedSlideshow } from "@/components/site/brollop/BrollopFullBleedSlideshow";


const DESTINATIONS: { city: string; region: string; note: string }[] = [
  { city: "Stockholm", region: "Stockholms län", note: "Skärgård, herrgårdar, Gamla stan. Vi kör utan reseutlägg inom tullarna." },
  { city: "Göteborg", region: "Västra Götaland", note: "Bohuslän, Marstrand och västkustklipporna. Vårt hemmaplan — recce ingår." },
  { city: "Malmö & Skåne", region: "Skåne län", note: "Slott, fält och kustbröllop. Övernattning bokas för dagar som börjar före 09." },
  { city: "Uppsala", region: "Uppsala län", note: "Universitetsstaden och herrgårdarna runt Mälaren." },
  { city: "Destination weddings", region: "Europa", note: "Italien, Frankrike, Spanien. Separat kalkyl för resa, frakt och permits." },
];


const FAQ = [
  {
    q: "Vad kostar en bröllopsfilm hos Fenny?",
    a: "Vårt Cinematic-paket börjar på 85 000 kr och Heritage Documentary på 125 000 kr. Slutpris ligger typiskt 85 000–150 000 kr beroende på dag, plats och tillval. Resa, logi, mat och eventuell hyra av specialutrustning tillkommer.",
  },
  {
    q: "Hur många bröllop tar ni per år?",
    a: "Vi tar 1–3 bröllop per år. Det är medvetet — vi vill att varje par ska få vår fulla uppmärksamhet både före, under och efter dagen. Vi tackar nej till de flesta förfrågningar.",
  },
  {
    q: "När får vi filmen?",
    a: "Highlight-film, ceremoni-cut och teaser levereras inom 8–12 veckor. Dokumentär långfilm (Heritage) ligger i den övre delen av spannet. Behövs något snabbare löser vi det.",
  },
  {
    q: "Får vi råmaterialet?",
    a: "Råmaterialarkiv på extern hårddisk ingår i Heritage-paketet och finns som tillval (+4 000 kr) till Cinematic. Vi rekommenderar det — det är ett arkiv för livet.",
  },
  {
    q: "Filmar ni utomlands?",
    a: "Ja. Destinationsbröllop går utmärkt — resa, logi, frakt av utrustning och eventuella permits faktureras separat utöver paketpriset. Vi gör en separat kalkyl per land och destination.",
  },
  {
    q: "Vad händer om filmaren blir sjuk?",
    a: "Vi är ett produktionsbolag, inte en enskild filmare. Vid akut sjukdom har vi backup från vårt eget nätverk av filmare som arbetar i samma estetik. Backup-utrustning finns alltid med på plats.",
  },
  {
    q: "Är ni försäkrade?",
    a: "Ja — ansvars- och utrustningsförsäkring genom Folksam. Vi delar gärna försäkringsbevis vid behov.",
  },
  {
    q: "Hur väljer ni vilka bröllop ni tar?",
    a: "Vi behöver känna att det blir en bra match. Det betyder inte att ni måste vara filmkunniga — bara att ni vill ha ett dokumentärt och filmiskt resultat snarare än en redigerad version av dagen. Vi börjar alltid med ett samtal.",
  },
  {
    q: "Hur många filmare är på plats?",
    a: "Standard är två filmare med var sin cinema-kamera, plus drönare där det är tillåtet. Vid större bröllop (150+ gäster eller flera samtidiga moment) lägger vi till en tredje filmare. Vi har alltid full backup-utrustning med oss.",
  },
  {
    q: "Tar ni stillbilder också?",
    a: "Nej — vi är ett filmteam och fokuserar helt på rörlig bild. Däremot samarbetar vi gärna med er fotograf och har ett nätverk av fotografer i samma estetik som vi kan rekommendera om ni behöver.",
  },
  {
    q: "Hur stör ni minst möjligt under ceremonin?",
    a: "Tysta kameror, långa optik, fasta positioner och inga blixtljus. Vi pratar igenom med präst eller vigselförrättare före, kollar tillåtna positioner och rör oss aldrig mitt i en ceremoni. Gäster ska knappt märka att vi är där.",
  },
  {
    q: "Kan vi få en kortare social-version till Instagram?",
    a: "Ja — en 60–90 sek vertikal teaser ingår i båda paketen, optimerad för Reels och TikTok. Klippt med samma musik och färg som highlight-filmen.",
  },
  {
    q: "Vilken musik används i filmerna?",
    a: "Vi licensierar musik via Musicbed och Artlist — kommersiell licens som täcker både privat visning och sociala kanaler. Vi väljer musik tillsammans med er före leverans.",
  },
  {
    q: "Vad händer om vädret blir uselt?",
    a: "Vi har plan B på recce. Regn ger ofta de vackraste bilderna — vi har vattentätt skydd för all utrustning och flyttar utomhusmoment inomhus eller under tak utan stress.",
  },
];

export const Route = createFileRoute("/brollopsfilm")({
  head: () => ({
    meta: [
      { title: "Bröllopsfilm — cinematic och dokumentär | Fenny" },
      {
        name: "description",
        content:
          "Premium bröllopsfilm av Fenny. Cinematic highlight och dokumentär långfilm. Från 85 000 kr i Sverige. Vi tar 1–3 bröllop per år.",
      },
      { property: "og:title", content: "Bröllopsfilm — Fenny" },
      {
        property: "og:description",
        content:
          "Cinematic + Heritage Documentary. Bröllopsfilm av samma team som filmar för Bolt, Huawei och Garageportexperten. Från 85 000 kr.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/brollopsfilm" },
      ...ogImageMeta({
        hub: "brollopsfilm",
        alt: "Brudpar går mot herrgård i guldljus — cinematic bröllopsfilm av Fenny.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/brollopsfilm" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/brollopsfilm", name: "Bröllopsfilm — cinematic och dokumentär | Fenny", description: "Premium bröllopsfilm av Fenny. Cinematic highlight och dokumentär långfilm. Från 85 000 kr i Sverige. Vi tar 1–3 bröllop per år."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Bröllopsfilm — Fenny",
            path: "/brollopsfilm",
            serviceType: "Bröllopsfilmsproduktion",
            description:
              "Premium bröllopsfilm — cinematic highlight och dokumentär långfilm. Multi-kamera, drönare, professionellt ljud. 1–3 uppdrag per år, från 85 000 kr i Sverige.",
            priceRange: "85000-150000+ SEK",
            areaServed: [
              "Stockholm",
              "Göteborg",
              "Malmö",
              "Skåne",
              "Uppsala",
              "Sverige",
              "Europa",
            ],
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          videoObject({
            name: "Bröllopsfilm av Fenny — exempel",
            description:
              "Utdrag från en dokumentär bröllopsfilm av Fenny. Cinematic foto, dokumentär närvaro och färgsatt grade — samma hantverk och team som ligger bakom våra varumärkesproduktioner.",
            thumbnailUrl: "https://i.ytimg.com/vi/2efcFS-3yHU/hqdefault.jpg",
            uploadDate: "2025-09-01",
            duration: "PT2M",
            embedUrl: "https://www.youtube.com/embed/2efcFS-3yHU",
            contentUrl: "https://www.youtube.com/watch?v=2efcFS-3yHU",
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(faqPage(FAQ)),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Bröllopsfilm", path: "/brollopsfilm" },
          ]),
        ),
      },
    ],
  }),
  component: BrollopsfilmPage,
});

function BrollopsfilmPage() {
  return (
    <article>
      {/* Hero — cinematic full-bleed video bakgrund */}
      <section
        id="hero"
        className="relative isolate overflow-hidden bg-black"
      >
        <BrollopHeroVideo
          id="2efcFS-3yHU"
          poster="https://i.ytimg.com/vi/2efcFS-3yHU/hqdefault.jpg"
        />
        {/* Färg- och djupgradienter */}
        <div className="pointer-events-none absolute inset-0 bg-black/55" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-black/20" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        {/* Mjuk filmkorn-känsla */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay [background-image:radial-gradient(rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:3px_3px]" />

        <div className="container-wide relative z-10 grid min-h-[88vh] grid-cols-12 items-center gap-8 pt-40 pb-20 md:min-h-[92vh] md:pt-48 md:pb-28">
          <div className="col-span-12 lg:col-span-7">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-white/85 backdrop-blur-sm">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inset-0 animate-ping rounded-full bg-mint/70" />
                <span className="relative h-1.5 w-1.5 rounded-full bg-mint" />
              </span>
              Bröllopsfilm · 1–3 per år
            </div>
            <h1 className="text-balance text-4xl font-semibold leading-[1.05] text-white drop-shadow-[0_4px_30px_rgba(0,0,0,0.45)] sm:text-5xl md:text-7xl">
              Bröllopsfilm i samma hantverk som varumärken betalar för.
            </h1>
            <p className="mt-6 max-w-2xl text-pretty text-base text-white/85 md:text-lg">
              Vi är ett produktionsbolag, inte en bröllopsfilmare. Samma team
              som filmar för Bolt, Huawei och Garageportexperten kommer hem till
              er dag — med rätt utrustning, rätt erfarenhet och den lugna närvaron
              som krävs för att fånga något äkta.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Button to="/kontakt" variant="mint" arrow>
                Berätta om er dag
              </Button>
              <Button href="#film" variant="outline">
                Se filmen
              </Button>
              <span className="text-xs uppercase tracking-[0.2em] text-white/55">
                Från 85 000 kr · Hela Sverige
              </span>
            </div>
          </div>

          {/* Polaroid stack — sitter bredvid texten, samma grid */}
          <div className="hidden lg:col-span-5 lg:block">
            <BrollopFloatingStills />
          </div>
        </div>

        {/* Liten meta-rad nere till höger — som en kameraöverlägg */}
        <div className="pointer-events-none absolute bottom-6 right-6 z-10 hidden items-center gap-2 rounded-full border border-white/15 bg-black/40 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-white/70 backdrop-blur md:inline-flex">
          <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
          Utdrag · Dokumentär bröllopsfilm
        </div>
      </section>

      {/* Snabb översikt */}
      <Section className="py-10">
        <div className="mx-auto max-w-5xl">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-mint">
                Snabb översikt
              </div>
              <h2 className="mt-1 text-2xl font-semibold tracking-tight md:text-3xl">
                Bröllopsfilm av Fenny — i korthet
              </h2>
            </div>
          </div>
          <div className="grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 md:grid-cols-4">
            {[
              {
                label: "Format",
                value: "Cinematic highlight",
                sub: "eller Heritage Documentary",
              },
              {
                label: "Längd",
                value: "6–9 min",
                sub: "+ 25–45 min dokumentär (Heritage)",
              },
              {
                label: "Produktion",
                value: "Multi-kamera",
                sub: "Drönare & professionellt ljud",
              },
              {
                label: "Pris i Sverige",
                value: "85–150 000 kr",
                sub: "Resa, logi & ev. hyra tillkommer",
              },
            ].map((item) => (
              <div
                key={item.label}
                className="bg-background/95 p-6"
              >
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/50">
                  {item.label}
                </div>
                <div className="mt-2 text-lg font-semibold text-white">
                  {item.value}
                </div>
                <div className="mt-1 text-sm leading-relaxed text-white/65">
                  {item.sub}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Värdebärande pillars */}
      <Section className="py-10">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              title: "Dokumentärt först",
              body: "Vi regisserar inte er dag. Vi rör oss tyst, fångar reaktioner som de blir och berättar dagen så som ni faktiskt levde den.",
            },
            {
              title: "Filmiskt hantverk",
              body: "Cinema-kameror, snabba primes, gimbal, drönare där det är tillåtet. Färgsatt manuellt — inte ett preset. Hela kedjan är produktionsbolagskvalitet.",
            },
            {
              title: "1–3 bröllop per år",
              body: "Vi tackar nej till de flesta förfrågningar. När vi tackar ja är ni vår enda fokus den månaden — från recce till färdig leverans.",
            },
          ].map((b) => (
            <div
              key={b.title}
              className="rounded-2xl border border-white/10 bg-surface/40 p-6"
            >
              <div className="mb-2 text-xs uppercase tracking-widest text-mint">
                {b.title}
              </div>
              <p className="text-pretty text-base text-foreground/85">{b.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Film — exempelfilm i hög upplösning ovanför paketen */}
      <Section className="py-10" id="film">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-mint">
              Exempelfilm
            </div>
            <h2 className="mt-1 text-balance text-2xl font-semibold tracking-tight md:text-3xl">
              Se hur en bröllopsfilm av Fenny känns.
            </h2>
          </div>
          <p className="hidden max-w-sm text-sm text-foreground/70 md:block">
            Utdrag ur en dokumentär bröllopsfilm — samma färgsättning,
            ljudbild och dramaturgi ni får i ert paket.
          </p>
        </div>
        <div className="relative aspect-video overflow-hidden rounded-3xl border border-white/10 bg-black shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)]">
          <iframe
            title="Bröllopsfilm av Fenny — exempel"
            src="https://www.youtube-nocookie.com/embed/2efcFS-3yHU?autoplay=1&mute=1&loop=1&playlist=2efcFS-3yHU&rel=0&modestbranding=1&playsinline=1"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 h-full w-full border-0"
          />
        </div>
      </Section>

      <div id="paket">
        <BrollopPackages />
      </div>

      <BrollopTimeline />

      {/* Full-bleed stillbilder — lättar upp grafiken mellan Dagen och offert */}
      <BrollopFullBleedSlideshow />

      <BrollopCalculator />

      {/* Process */}
      <Section className="py-10" id="process">
        <div className="mx-auto max-w-4xl">
          <EyebrowHeading
            eyebrow="Process"
            title={<>Från första samtalet till färdig film.</>}
          />
          <ol className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                label: "Match-samtal",
                duration: "30 min",
                body: "Vi pratar om er dag, er stil och vad ni vill minnas. Antingen passar vi för varandra eller så hjälper vi er hitta någon som gör det.",
              },
              {
                label: "Recce + planering",
                duration: "6 v. före",
                body: "Vi besöker platsen, går igenom tidsschema och nyckelmoment med er och er koordinator. Backup-planer på plats.",
              },
              {
                label: "Bröllopsdagen",
                duration: "10–12 h",
                body: "Vi är på plats från getting ready till fest. Diskret, närvarande, alltid två backup-kameror redo.",
              },
              {
                label: "Leverans",
                duration: "8–12 v. efter",
                body: "Highlight först, sedan ceremoni-cut och teaser. Heritage-långfilmen ligger sist i spannet. Allt levererat i 4K via privat länk.",
              },
            ].map((s, i) => (
              <li
                key={s.label}
                className="relative rounded-2xl border border-white/10 bg-surface/40 p-5"
              >
                <div className="text-xs uppercase tracking-widest text-mint">
                  {String(i + 1).padStart(2, "0")}
                  <span className="ml-2 text-muted-foreground">· {s.duration}</span>
                </div>
                <div className="mt-2 text-lg font-semibold text-foreground">
                  {s.label}
                </div>
                <p className="mt-2 text-sm leading-relaxed text-foreground/80">
                  {s.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </Section>

      {/* Stillbilder — känsla & hantverk */}
      <Section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl">
          <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-mint">
                Stillbilder
              </div>
              <h2 className="mt-2 text-balance text-2xl font-semibold md:text-3xl">
                Ögonblicken vi letar efter.
              </h2>
            </div>
            <p className="hidden max-w-sm text-sm text-foreground/75 md:block">
              Riktiga stills från riktiga bröllop — samma färgsättning och
              dokumentära blick som ni får i er färdiga film.
            </p>
          </div>
          <BrollopMoodStrip />
        </div>
      </Section>

      {/* Vad ingår inte */}
      <Section className="py-10">
        <div className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-surface/30 p-8 md:p-10">
          <div className="mb-3 text-[11px] uppercase tracking-widest text-muted-foreground">
            Transparent
          </div>
          <h2 className="text-balance text-2xl font-semibold md:text-3xl">
            Det här ingår inte i paketpriset.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-foreground/85">
            För att 85 000–150 000 kr ska räcka till det vi faktiskt levererar
            håller vi paketen rena. Följande hanteras separat per offert:
          </p>
          <ul className="mt-5 space-y-2 text-sm text-foreground/80">
            <li>• Resa till bröllopsplatsen (bil, tåg, flyg)</li>
            <li>• Logi för crewet om dagen kräver övernattning</li>
            <li>• Mat på plats (vi tackar ja om ni erbjuder)</li>
            <li>
              • Hyra av specialutrustning — t.ex. extra ljus för mörka kyrkor,
              gimbal-operatör som specialist eller specifika optik
            </li>
            <li>
              • Tillstånd för drönare på platser som kräver särskild ansökan
            </li>
          </ul>
          <p className="mt-5 text-xs italic text-muted-foreground">
            Vi gör en separat kalkyl efter recce. Inga överraskningar i fakturan
            efter dagen.
          </p>
        </div>
      </Section>

      {/* Destinations */}
      <Section className="py-10" id="destinationer">
        <EyebrowHeading
          eyebrow="Var vi filmar"
          title={<>Bröllopsfilmare för hela Sverige — och dit ni reser.</>}
        />
        <p className="mx-auto mt-4 max-w-3xl text-pretty text-base text-foreground/80">
          Vi har bas i Göteborg och tar uppdrag i hela Sverige samt destinationsbröllop
          i Europa. Resa, logi och eventuell frakt av utrustning faktureras separat
          enligt recce — inga schablonpåslag.
        </p>
        <div className="mx-auto mt-10 grid max-w-5xl gap-4 md:grid-cols-2 lg:grid-cols-3">
          {DESTINATIONS.map((d) => (
            <div
              key={d.city}
              className="rounded-2xl border border-white/10 bg-surface/40 p-5"
            >
              <div className="text-xs uppercase tracking-widest text-mint">
                {d.region}
              </div>
              <div className="mt-2 text-lg font-semibold text-foreground">
                Bröllopsfilmare i {d.city}
              </div>
              <p className="mt-2 text-sm leading-relaxed text-foreground/80">
                {d.note}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section className="py-10" id="faq">
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-10 md:grid-cols-[1fr_1.5fr]">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-mint">
                Vanliga frågor
              </div>
              <h2 className="mt-3 text-balance text-3xl font-semibold leading-[1.05] md:text-5xl">
                Det vi får mest av.
              </h2>
              <p className="mt-5 text-pretty text-base text-foreground/75">
                Hittar ni inte svaret? Skriv direkt till oss — vi svarar
                personligt, normalt inom 24 timmar inom kontorstid
                (vardagar 08:30–17:00).
              </p>
              <div className="mt-6">
                <Button to="/kontakt" variant="mint" arrow>
                  Ställ er fråga
                </Button>
              </div>
            </div>

            <div className="divide-y divide-white/10 rounded-3xl border border-white/10 bg-surface/40">
              {FAQ.map((f, idx) => (
                <details
                  key={f.q}
                  className="group px-6 py-5 transition-colors open:bg-surface/60 md:px-8"
                >
                  <summary className="flex cursor-pointer list-none items-start gap-4 text-left text-base font-medium text-foreground md:text-lg">
                    <span className="mt-1 inline-block w-8 shrink-0 text-xs uppercase tracking-[0.18em] text-mint">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1 text-pretty">{f.q}</span>
                    <span
                      aria-hidden
                      className="mt-1.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-white/15 text-foreground/70 transition-transform group-open:rotate-45"
                    >
                      +
                    </span>
                  </summary>
                  <p className="mt-4 pl-12 text-pretty text-base leading-relaxed text-foreground/80">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Closing */}
      <Section>
        <div className="rounded-3xl border border-white/10 bg-surface/60 p-8 text-center md:p-12">
          <h2 className="text-balance text-3xl font-semibold md:text-4xl">
            Vi tar 1–3 bröllop per år.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
            Berätta om er dag — datum, plats, känsla. Vi svarar inom två
            arbetsdagar och bokar ett samtal om vi tror att matchningen stämmer.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Button to="/kontakt" variant="mint" arrow>
              Skicka förfrågan
            </Button>
            <Button href="#kalkylator" variant="outline">
              Räkna estimat
            </Button>
          </div>
        </div>
      </Section>

      <ContactForm location="landing_brollopsfilm" />
    </article>
  );
}
